import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  Button,
  Tag,
  Space,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import { workerListService } from '@iso/services';
import { OPS_COLORS } from './OperationsUI';

const { Option } = Select;

const TYPE_META = {
  employee: { label: 'Employee', color: '#067647' },
  contractor: { label: 'Contractor', color: 'gold' },
};

/**
 * The worker-list management UI (list + add contractor / edit / remove / re-add),
 * self-contained so it can be used both as the standalone Workers page and
 * inside a modal (e.g. from Team Schedule). Owner/admin gating mirrors the
 * backend. `onChanged` fires after any successful mutation so a host screen can
 * refresh its own copy of the worker list.
 */
const WorkersManager = ({ onChanged }) => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activePermission = useSelector((state) => state.permission.activePermission);
  const canManage = ['owner', 'admin'].includes(activePermission);

  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const loadWorkers = () => {
    if (!activePropertyId) return;
    setLoading(true);
    workerListService
      // Managers see removed workers too (so they can be re-added).
      .getList(activePropertyId, canManage)
      .then((res) => setWorkers(res?.workers || []))
      .catch(() => message.error('Could not load workers'))
      .finally(() => setLoading(false));
  };

  useEffect(loadWorkers, [activePropertyId, canManage]);

  // Reload locally and let the host screen refresh too.
  const afterChange = () => {
    loadWorkers();
    if (onChanged) onChanged();
  };

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: 'contractor' });
    setModalOpen(true);
  };

  const openEdit = (worker) => {
    setEditing(worker);
    form.setFieldsValue({
      name: worker.name,
      email: worker.email,
      phone: worker.phone,
      type: worker.type,
    });
    setModalOpen(true);
  };

  const handleSubmit = (values) => {
    setSubmitting(true);
    const req = editing
      ? workerListService.update(editing.id, {
          name: values.name,
          email: values.email || null,
          phone: values.phone || null,
          type: values.type,
        })
      : workerListService.create({
          property_id: activePropertyId,
          name: values.name,
          email: values.email || null,
          phone: values.phone || null,
          type: values.type || 'contractor',
        });
    req
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(editing ? 'Worker updated' : 'Worker added');
          setModalOpen(false);
          afterChange();
        } else {
          message.error(res?.message || 'Could not save worker');
        }
      })
      .catch(() => message.error('Could not save worker'))
      .finally(() => setSubmitting(false));
  };

  const handleRemove = (worker) => {
    workerListService
      .destroy(worker.id)
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(`${worker.name} removed from the worker list`);
          afterChange();
        } else {
          message.error(res?.message || 'Could not remove worker');
        }
      })
      .catch(() => message.error('Could not remove worker'));
  };

  const handleRestore = (worker) => {
    workerListService
      .restore(worker.id)
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(`${worker.name} re-added`);
          afterChange();
        } else {
          message.error(res?.message || 'Could not re-add worker');
        }
      })
      .catch(() => message.error('Could not re-add worker'));
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (_, w) => (
        <span style={{ opacity: w.is_active ? 1 : 0.5 }}>
          <UserOutlined style={{ marginRight: 8, color: OPS_COLORS.grey }} />
          <strong>{w.name}</strong>
        </span>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 110,
      render: (_, w) => {
        const meta = TYPE_META[w.type] || { label: w.type, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Source',
      key: 'source',
      width: 165,
      render: (_, w) =>
        w.is_linked ? <Tag>App user</Tag> : <Tag color="blue">Contractor (no login)</Tag>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true, render: (v) => v || '—' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 120, render: (v) => v || '—' },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, w) =>
        w.is_active ? <Tag color="#067647">Active</Tag> : <Tag color="red">Removed</Tag>,
    },
    ...(canManage
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 150,
            align: 'right',
            render: (_, w) =>
              w.is_active ? (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(w)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title={`Remove ${w.name} from the worker list?`}
                    onConfirm={() => handleRemove(w)}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="link" size="small" danger>
                      Remove
                    </Button>
                  </Popconfirm>
                </Space>
              ) : (
                <Button type="link" size="small" onClick={() => handleRestore(w)}>
                  Re-add
                </Button>
              ),
          },
        ]
      : []),
  ];

  // Editing a linked user's name/email doesn't make sense (owned by the user
  // account); only free-form contractors expose those fields for editing.
  const detailsEditable = !editing || !editing.is_linked;

  return (
    <div>
      {canManage ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add Contractor
          </Button>
        </div>
      ) : null}

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={workers}
          pagination={false}
          size="small"
          tableLayout="fixed"
          locale={{ emptyText: 'No workers yet' }}
        />
      </Spin>

      <Modal
        title={editing ? 'Edit Worker' : 'Add Contractor'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText={editing ? 'Save' : 'Add'}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, whitespace: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g. Dan Fencing Contractor" disabled={!detailsEditable} />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select>
              <Option value="employee">Employee</Option>
              <Option value="contractor">Contractor</Option>
            </Select>
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input placeholder="Optional" disabled={!detailsEditable} />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="Optional" disabled={!detailsEditable} />
          </Form.Item>
          {editing && editing.is_linked ? (
            <div style={{ color: OPS_COLORS.grey, fontSize: 13 }}>
              This worker is linked to an app user, so their name and email come
              from their account.
            </div>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default WorkersManager;
